/**
 * ******************************************************
 * Copyright (C) 2015-2020 VMware, Inc. All rights reserved.
 * *******************************************************
 *
 * @format
 */

/**
 * @fileoverview mediacapture.js -- MediaCapture
 * Class based on AudioCaptuer & VideoCapture to handle media capturing
 * depend on AudioCapture, VideoCapture, and SyncTimer
 *
 * the callback is used to pull the next video frame, while audio need no such
 *    an property since it push the data out.
 *
 * API:
 * No init API, after new or clear, the instance should be in the started
 *    status
 * We need to detect stopstream instead of dealing with that message is caused
 *    by the fact that the agent doesn't implement that message.
 *
 * open: get device
 * close: release device, Close the instance with all hardware resources
 *    released.
 * startStream: get device and ready to capture any data
 * stopCapturing: reset and return to the original status(started) after
 *    created
 * enable: enable the capturing of data of specified type
 * disable: disable the capturing of data of specified type
 */

import RTAV from "./logservice";

export default function MediaCapture() {
   let supportHTML5Audio, MediaStream;

   window.AudioContext =
      window.AudioContext ||
      window.webkitAudioContext ||
      window.mozAudioContext;
   navigator.getUserMedia =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia ||
      navigator.mediaDevices.getUserMedia;

   supportHTML5Audio = !!window.AudioContext && !!navigator.getUserMedia;
   if (!supportHTML5Audio) {
      RTAV.logService.error(
         "browser does not support HTML5 media in, creating media source fails"
      );
      return null;
   }
   RTAV.logService.log("browser supports HTML5 media in");
   /**
    * MediaStream is an experimental technology, so add this compatible codes
    * to provide the same API for the new and old browsers
    */
   MediaStream = window.MediaStream || window.webkitMediaStream;
   if (MediaStream !== undefined) {
      if (!MediaStream.prototype.hasOwnProperty("stop")) {
         MediaStream.prototype.stop = function() {
            this.getAudioTracks().forEach(function(track) {
               track.stop();
            });
            this.getVideoTracks().forEach(function(track) {
               track.stop();
            });
         };
      }
   }

   this.hasBlockedStartStream = false;
   this.syncTimer = new RTAV.SyncTimer();
   this.audioCapture = new RTAV.AudioCapture();
   this.videoCapture = new RTAV.VideoCapture();
   if (!this.audioCapture || !this.videoCapture || !this.syncTimer) {
      RTAV.logService.error(
         "capture instance is not created, creating media source fails"
      );
      return null;
   }
   this.streamStarted = false;
   this.isEnabled = {
      audio: false,
      video: false
   };
   this.resourcesHold = {
      audio: false,
      video: false
   };
   this.localStream = {
      audio: null,
      video: null
   };
   RTAV.logService.log("capture instance initialized");
}

/**
 * Stop both video and audio if possible.
 * @private
 */
MediaCapture.prototype.disableAll = function() {
   if (this.isEnabled.audio) {
      this.disable("audio");
   }
   if (this.isEnabled.video) {
      this.disable("video");
   }
};

/**
 * Close the instance with all hardware resources released.
 * @param  {string} type The type name of device of the request
 */
MediaCapture.prototype.close = function(type) {
   if (!this.resourcesHold[type]) {
      RTAV.logService.debug("no need to close " + type + " device");
      this.localStream[type] = null;
      return;
   }
   if (
      !!this.localStream[type] &&
      typeof this.localStream[type].stop === "function"
   ) {
      this.localStream[type].stop();
      RTAV.logService.log(
         "rtav source is closed, " + type + " hardware get released"
      );
   } else {
      RTAV.logService.log(
         "rtav source is closed, but " +
            type +
            " hardware might fail to be released"
      );
   }
   this.localStream[type] = null;
   this.resourcesHold[type] = false;
   this.hasBlockedStartStream = false;
};

/**
 * Stop capture audio and video, and release the devices
 * after clear this instance should look like a new object
 */
MediaCapture.prototype.stopCapturing = function() {
   // ensure stop
   if (this.streamStarted) {
      RTAV.logService.debug(
         "negative: there is at least one of stream capture session, so disable them before stopping capture"
      );
      this.disableAll();
   }

   // clear audio/video capture
   this.syncTimer.clear();
   if (
      this.audioCapture.getStatus() !== RTAV.AudioCapture.statusEnum["Uninited"]
   ) {
      this.audioCapture.clear();
      RTAV.logService.debug("audio stream stopped");
   }
   if (
      this.videoCapture.getStatus() !== RTAV.VideoCapture.statusEnum["Uninited"]
   ) {
      this.videoCapture.clear();
      RTAV.logService.debug("video stream stopped");
   }
   this.streamStarted = false;
   this.hasBlockedStartStream = false;
   this.isEnabled.audio = false;
   this.isEnabled.video = false;
   RTAV.logService.debug("capture stream stopped");
};

/**
 * Async process to get control of audio & video devices
 * @param {string} type The device tpye name of open request
 * @param {function} resolve The callback with a bool as open succcess or not
 * @param {object} deviceMessageController This param is used to emit
 *    permission dialog related events
 */
MediaCapture.prototype.open = function(type, resolve, deviceMessageController) {
   let deviceRequest = {
         audio: false,
         video: false
      },
      successCallback,
      errorCallback;

   successCallback = function(stream) {
      RTAV.logService.log("obtain the rtav device for " + type);
      this.localStream[type] = stream;
      this.resourcesHold[type] = true;
      resolve(true);
      deviceMessageController.onPermissionHandled(type);
   }.bind(this);
   errorCallback = function(e) {
      RTAV.logService.log(
         "negative: can't get the rtav device, so rtav will not working" + e
      );
      resolve(false);
      deviceMessageController.onPermissionHandled(type);
   };

   RTAV.preferredDeviceService
      .getPreferredDeviceId(type)
      .then((preferredId) => {
         if (preferredId) {
            deviceRequest[type] = {
               deviceId: preferredId
            };
         } else {
            deviceRequest[type] = true;
         }
         RTAV.logService.debug("requesting device with request: " + JSON.stringify(deviceRequest));
         if (!this.resourcesHold[type]) {
            if (!!navigator && !!navigator.mediaDevices && navigator.mediaDevices.hasOwnProperty("getUserMedia") === true) {
               navigator.mediaDevices.getUserMedia(
                  deviceRequest,
                  successCallback,
                  errorCallback
               );
            } else {
               navigator.getUserMedia(
                  deviceRequest,
                  successCallback,
                  errorCallback
               );
            }
            deviceMessageController.onPermissionAsked(type);
         }
      });
};

/**
 * Start capture audio and video
 * This function fit for the message startstream, and will ensure both the
 * audio and video source inited
 * @param  {function} audioCallback The callback funciton used to deal with
 *    cauptured audio
 * @param  {function} videoCallback The callback funciton used to deal with
 *    cauptured video
 */
MediaCapture.prototype.startStream = function(
   deviceParam,
   audioCallback,
   videoCallback
) {
   if (this.streamStarted) {
      RTAV.logService.debug(
         'the media capture has already being "streamStarted" status, skip start'
      );
      return;
   }
   if (!this.isEnabled.audio && !this.isEnabled.video) {
      RTAV.logService.debug("previous hasBlockedStartStream is " + this.hasBlockedStartStream);
      this.hasBlockedStartStream = true;
      RTAV.logService.error(
         "Negative: the media capture try to start with none of the video nor audio enabled, would check again when device obtained"
      );
      return;
   }

   if (!this.resourcesHold.audio && !this.resourcesHold.video) {
      RTAV.logService.log(
         "no stream hold before trying to start capturing, please check whether video/audio device exist"
      );
      return;
   }

   RTAV.logService.debug("starting the source stream");

   this.syncTimer.reset();
   this.audioCapture.init(
      deviceParam.audio,
      this.syncTimer,
      function(sample) {
         audioCallback(sample);
      }.bind(this)
   );
   this.videoCapture.init(
      deviceParam.video,
      this.syncTimer,
      function(sample) {
         videoCallback(sample);
      }.bind(this)
   );

   // we need to start start the audio/video capturing if that is enabled
   if (this.isEnabled.audio) {
      RTAV.logService.debug("start capturing audio data");
      this.audioCapture.start(this.localStream["audio"]);
   }
   if (this.isEnabled.video) {
      RTAV.logService.debug("start capturing video data");
      this.videoCapture.start(this.localStream["video"]);
   }
   this.streamStarted = true;
};

/**
 * Disable the capturing of data of specified type
 * @param  {string} type This param is one of the 'audio' and 'video'.
 * @returns {Promise<boolean>} whether action succeeded
 */
MediaCapture.prototype.disable = function(type) {
   return new Promise((resolve) => {
      if (!this.resourcesHold[type]) {
         RTAV.logService.log(
            "call disable when no device hold, video device might be missing"
         );
      }

      RTAV.logService.debug(
         "disable " +
            type +
            " when stream " +
            (this.streamStarted ? "have" : "haven't") +
            " started"
      );
      if (this.streamStarted) {
         if (type === "audio") {
            this.audioCapture.stop();
         } else if (type === "video") {
            this.videoCapture.stop();
         }
         this.close(type);
      } else {
         RTAV.logService.log(
            "Negative: no stream started when disable " + type
         );
         resolve(false);
         return;
      }
      RTAV.logService.debug(
         "media source working status had been changed to disable for " + type
      );
      this.isEnabled[type] = false;
      /**
       * this code is to detect stopStream
       * The agent implement the stopstream this way, so client need to do work
       * accordingly. if detected, the source will stop working but that can be
       * recoveried by enable audio/video and startstream again.
       */
      if (!this.isEnabled.audio && !this.isEnabled.video) {
         this.streamStarted = false;
         RTAV.logService.debug(
            "stop capture stream since neither audio nor video are used"
         );
         this.stopCapturing();
      }
      RTAV.logService.log("media source disable done for " + type);
      resolve(true);
   });
};

/**
 * Enable the capturing of data of specified type
 * @param  {string} type This param is one of the 'audio' and 'video'.
 * @param  {object} deviceMessageController This param is used to emit
 *    premission dialog related events
 * @returns {Promise<boolean>} whether action succeeded
 */
MediaCapture.prototype.enable = function(type, deviceMessageController) {
   return new Promise((resolve) => {
      if (this.isEnabled[type]) {
         RTAV.logService.error(
            "Negative: try to re-enable a media source of type " + type
         );
         resolve(false);
         return;
      }

      this.open(
         type,
         function(success) {
            if (success) {
               if (this.streamStarted) {
                  if (type === "audio") {
                     this.audioCapture.start(this.localStream[type]);
                  } else if (type === "video") {
                     this.videoCapture.start(this.localStream[type]);
                  }
               }
               RTAV.logService.debug(
                  "media source working status changed to enable for " + type
               );
               this.isEnabled[type] = true;
            }
            resolve(success);
         }.bind(this),
         deviceMessageController
      );
   });
};

/**
 * return whether the device of given type is active
 * @param  {string} type This param is one of the 'audio' and 'video'.
 */
MediaCapture.prototype.isActive = function(type) {
   if (type === "audio") {
      return this.audioCapture.isActive();
   } else if (type === "video") {
      return this.videoCapture.isActive();
   }
   return false;
};
